import {Graphooper} from "./Graphooper";
import {MarkerFeature} from "./MarkerFeature";
import {generateID} from "../helpers/IDGenerator";
import pointsForLineString from "../helpers/PointOnStraightLineHelper";
import {encode} from "@mapbox/polyline";
import mapboxgl from "mapbox-gl";
import {sanitizeCoords} from "../sanitizeCoords";
import {altitudesFromCoordinates, elevationGainFromAltitudes} from "../elevation/elevationTotals";
import {PROFILE_LINE, TurnInstruction, cumulativeDistancesMeters, mirroredInstructions, turnInstructionsFrom} from "./turnInstructions";

export class LineStringFeature {
  type: string = "Feature";
  lineStringGeometry: LineStringGeometry;
  lineStringProperties: LineStringProperties;
  bbox: number[];

  public constructor(
    lineStringGeometry: LineStringGeometry,
    lineStringProperties: LineStringProperties,
    bbox: number[]
  ) {
    this.lineStringGeometry = lineStringGeometry;
    this.lineStringProperties = lineStringProperties;
    this.bbox = bbox;
  }

  toWKT(distances?: number[]): string {
    const coords = this.lineStringGeometry.coordinates;
    const includeMeasure = Array.isArray(distances) && distances.length === coords.length;

    let wkt = "LINESTRING (";

    for (let i = 0; i < coords.length; i++) {
      const coordinate = coords[i];
      const altitude = coordinate[2] ?? 0.0;
      const parts: number[] = [coordinate[0], coordinate[1], altitude];

      if (includeMeasure) {
        const measure = distances![i] ?? 0;
        parts.push(measure);
      }

      wkt += parts.join(" ");
      if (i + 1 < coords.length) {
        wkt += ", ";
      }
    }

    wkt += ")";
    return wkt;
  }

  static straightLine(
    startMarker: MarkerFeature,
    endMarker: MarkerFeature
  ): LineStringFeature {
    let points = pointsForLineString(startMarker.markerGeometry.coordinates[1], startMarker.markerGeometry.coordinates[0], endMarker.markerGeometry.coordinates[1], endMarker.markerGeometry.coordinates[0]);
    const line = LineStringFeature.coordsOnlyFeature(points);
    // Also the placeholder while a snap is in flight, so a snap that never lands reads as the
    // straight line it left on the map.
    line.lineStringProperties.profile = PROFILE_LINE;
    return line;
  }

  static coordsOnlyFeature(coordinates: number[][]): LineStringFeature {
    const sanitizedCoords = sanitizeCoords(coordinates);
    // Ensure linestrings always contain at least two points to satisfy backend validation
    if (sanitizedCoords.length === 1 && coordinates.length > 0) {
      sanitizedCoords.push([...sanitizedCoords[0]]);
    }
    let properties = new LineStringProperties(
      0,
      0,
      [],
      new LineStringPropertiesExtra(
        new LineStringPropertiesSurface([], []),
        new LineStringPropertiesWaytypes([], []),
        []
      ),
      new LineStringPropertiesSummary(0, 0),
      []
    )
    properties.id = generateID()
    return new LineStringFeature(
      new LineStringGeometry(sanitizedCoords),
      properties,
      []
    );
  }

  static fromGeoJsonForEdit(geoJson: any): LineStringFeature {
    return LineStringFeature.coordsOnlyFeature(geoJson["geometry"]["coordinates"])
  }

  static fromGeoJson(geoJson: any): LineStringFeature {
    const rawCoords = geoJson["geometry"]["coordinates"] || [];
    const coords = rawCoords.length === 1 ? [...rawCoords, [...rawCoords[0]]] : rawCoords;

    return new LineStringFeature(
      new LineStringGeometry(coords),
      new LineStringProperties(
        geoJson["properties"]["ascent"],
        geoJson["properties"]["descent"],
        geoJson["properties"]["segments"],
        new LineStringPropertiesExtra(
          new LineStringPropertiesSurface(
            geoJson["properties"]["extras"]["surface"]["values"],
            geoJson["properties"]["extras"]["surface"]["summary"]
          ),
          new LineStringPropertiesWaytypes(
            geoJson["properties"]["extras"]["waytypes"]["values"],
            geoJson["properties"]["extras"]["waytypes"]["summary"]
          ),
          geoJson["properties"]["extras"]["distances"]
        ),
        new LineStringPropertiesSummary(
          geoJson["properties"]["summary"]["distance"],
          geoJson["properties"]["summary"]["duration"]
        ),
        geoJson["properties"]["wayPoints"]
      ),
      geoJson["bbox"]
    );
  }

  static fromGraphooper(graphooper: Graphooper, profile?: String): LineStringFeature {
    let path = graphooper.paths[0];

    // Deduplicate consecutive points from GraphHopper response
    const rawCoords = path.points.coordinates;
    const dedupedCoords = [];

    for (let i = 0; i < rawCoords.length; i++) {
      const coord = rawCoords[i];

      // Skip if this coordinate is identical to the previous one
      if (i > 0) {
        const prevCoord = dedupedCoords[dedupedCoords.length - 1];
        if (prevCoord &&
            coord[0] === prevCoord[0] &&
            coord[1] === prevCoord[1]) {
          continue; // Skip duplicate
        }
      }

      dedupedCoords.push(coord);
    }

    // Avoid single-point linestrings (e.g., when snap collapses start/end onto the same spot)
    if (dedupedCoords.length === 1) {
      dedupedCoords.push([...dedupedCoords[0]]);
    }

    let properties = new LineStringProperties(
      path.ascend,
      path.descend,
      [],
      new LineStringPropertiesExtra(
        new LineStringPropertiesSurface(path.details.surface, []),
        new LineStringPropertiesWaytypes([], []),
        path.details.distance
      ),
      new LineStringPropertiesSummary(path.distance, path.time / 1000),
      []
    );
    properties.id = generateID()
    // Measured against the coordinates as GraphHopper returned them, because that is what
    // its instruction intervals index. De-duplicating only removes points that add no
    // length, so the distances still describe the geometry saved below.
    properties.instructions = turnInstructionsFrom(path.instructions, rawCoords);
    if (profile) properties.profile = String(profile);
    return new LineStringFeature(
      new LineStringGeometry(dedupedCoords),
      properties,
      path.bbox
    );
  }

  toGeoJsonSource(): mapboxgl.GeoJSONSource {
    return {
      type: "geojson",
      // @ts-ignore
      data: this.toGeoJson(),
    };
  }

  toGeoJsonString() {
    return JSON.stringify(this.toGeoJson());
  }

  // The shared 6m moving-baseline rule rather than a local copy of it, so this cannot drift from
  // the ascent the planner, the server and the apps show for the same line.
  toElevationGain(): number {
    return elevationGainFromAltitudes(altitudesFromCoordinates(this.lineStringGeometry.coordinates))
  }

  toGeoJson() {
    return {
      type: "Feature",
      bbox: this.bbox,
      geometry: {
        type: "LineString",
        coordinates: this.lineStringGeometry.coordinates,
      },
      properties: {
        ascent: this.lineStringProperties.ascent,
        descent: this.lineStringProperties.descent,
        segments: [],
        extras: {
          surface: {
            values: this.lineStringProperties.extras.surface.values,
            summary: [],
          },
          waytypes: { values: [], summary: [] },
          distances: this.lineStringProperties.extras.distances,
        },
        summary: {
          distance: this.lineStringProperties.summary.distance,
          duration: this.lineStringProperties.summary.duration,
        },
        wayPoints: [],
      },
    };
  }

  toStaticMap(): String {
    const encodedPolyline = encode(this.lineStringGeometry.coordinates.map((coordinate) => [coordinate[1], coordinate[0]]));
    const path = "&path=stroke:red|fill:none|enc:" + encodedPolyline;
    return `https://api.maptiler.com/maps/topo-v2/static/auto/600x600.jpg?key=eiYbK75yQcfs8fMBLBi0${path}`;
  }

  /**
   * Creates a reversed copy of the LineStringFeature, preserving all properties including surface data
   */
  reverse(): LineStringFeature {
    // Reverse the coordinates
    const reversedCoords = [...this.lineStringGeometry.coordinates].reverse();
    const totalCoords = this.lineStringGeometry.coordinates.length;

    // Reverse and adjust surface values indices
    const reversedSurfaceValues = this.lineStringProperties.extras.surface.values.map(segment => {
      const [startIdx, endIdx, surfaceType] = segment;
      // Convert indices for reversed array
      const newEndIdx = totalCoords - startIdx;
      const newStartIdx = totalCoords - endIdx;
      return [newStartIdx, newEndIdx, surfaceType];
    }).reverse();

    // Reverse and adjust waytype values indices
    const reversedWaytypeValues = this.lineStringProperties.extras.waytypes.values.map(segment => {
      const [startIdx, endIdx, waytypeValue] = segment;
      // Convert indices for reversed array
      const newEndIdx = totalCoords - startIdx;
      const newStartIdx = totalCoords - endIdx;
      return [newStartIdx, newEndIdx, waytypeValue];
    }).reverse();

    // Create new properties with reversed data
    const reversedProperties = new LineStringProperties(
      this.lineStringProperties.descent, // Swap ascent and descent
      this.lineStringProperties.ascent,
      [...this.lineStringProperties.segments].reverse(),
      new LineStringPropertiesExtra(
        new LineStringPropertiesSurface(reversedSurfaceValues, [...this.lineStringProperties.extras.surface.summary]),
        new LineStringPropertiesWaytypes(reversedWaytypeValues, [...this.lineStringProperties.extras.waytypes.summary]),
        [...this.lineStringProperties.extras.distances].reverse()
      ),
      new LineStringPropertiesSummary(
        this.lineStringProperties.summary.distance,
        this.lineStringProperties.summary.duration
      ),
      [...this.lineStringProperties.wayPoints].reverse()
    );
    
    // Preserve the ID
    reversedProperties.id = generateID();
    // Turns and profile are not constructor arguments, so without this a reversed segment
    // silently loses its guidance.
    const profile = this.lineStringProperties.profile ?? null;
    const distances = cumulativeDistancesMeters(this.lineStringGeometry.coordinates);
    reversedProperties.instructions = mirroredInstructions(
      this.lineStringProperties.instructions ?? [],
      profile,
      distances.length > 0 ? distances[distances.length - 1] : 0
    );
    reversedProperties.profile = profile;

    return new LineStringFeature(
      new LineStringGeometry(reversedCoords),
      reversedProperties,
      [...this.bbox]
    );
  }
}

export class LineStringGeometry {
  type: string = "LineString";
  coordinates: number[][] = [];

  constructor(coordinates: number[][]) {
    this.coordinates = coordinates;
  }
}

export class LineStringProperties {
  ascent: number;
  descent: number;
  segments: number[];
  extras: LineStringPropertiesExtra;
  summary: LineStringPropertiesSummary;
  wayPoints: [];
  id: string | null = null;
  /**
   * Turns for this segment, positioned by metres from its start. Empty for a straight line
   * or a segment drawn before they were captured, which is how a route ends up reporting
   * that turn-by-turn isn't available rather than guessing at one.
   */
  instructions: TurnInstruction[] = [];
  /** "hike", "bike" or "car" — what the segment was snapped with. */
  profile: string | null = null;

  constructor(
    ascent: number,
    descent: number,
    segments: number[],
    extras: LineStringPropertiesExtra,
    summary: LineStringPropertiesSummary,
    wayPoints: []
  ) {
    this.ascent = ascent;
    this.descent = descent;
    this.segments = segments;
    this.extras = extras;
    this.summary = summary;
    this.wayPoints = wayPoints;
  }
}

export class LineStringPropertiesSummary {
  distance: number;
  duration: number;

  constructor(distance: number, duration: number) {
    this.distance = distance;
    this.duration = duration;
  }
}

export class LineStringPropertiesExtra {
  surface: LineStringPropertiesSurface;
  waytypes: LineStringPropertiesWaytypes;
  distances: number[][];

  constructor(
    surface: LineStringPropertiesSurface,
    waytypes: LineStringPropertiesWaytypes,
    distances: number[][]
  ) {
    this.surface = surface;
    this.waytypes = waytypes;
    this.distances = distances;
  }
}

export class LineStringPropertiesSurface {
  values: Array<(string | number)[]>;
  summary: number[];

  constructor(values: Array<(string | number)[]>, summary: number[]) {
    this.values = values;
    this.summary = summary;
  }

  surfaceIntToString(index: number): string | null {
    const surfaceInt = this.values[index][0];
    switch (surfaceInt) {
      case 0:
        return `unknown`;
      case 1:
        return `paved`;
      case 2:
        return `unpaved`;
      case 3:
        return `asphalt`;
      case 4:
        return `concrete`;
      case 5:
        return `cobblestone`;
      case 6:
        return `metal`;
      case 7:
        return `wood`;
      case 8:
        return `compactedGravel`;
      case 9:
        return `fineGravel`;
      case 10:
        return `gravel`;
      case 11:
        return `dirt`;
      case 12:
        return `ground`;
      case 13:
        return `ice`;
      case 14:
        return `pavingStones`;
      case 15:
        return `sand`;
      case 16:
        return `woodchips`;
      case 17:
        return `grass`;
      case 18:
        return `grassPaver`;
      default:
        return null;
    }
  }
}

export class LineStringPropertiesWaytypes {
  values: number[][];
  summary: number[];

  constructor(values: number[][], summary: number[]) {
    this.values = values;
    this.summary = summary;
  }
}
